import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Grid,
  Chip,
  Menu,
  MenuItem,
  IconButton,
} from '@mui/material';
import {
  createFair,
  getFairs,
  deleteFair,
  updateFair,
  startStopFair,
  countFairsInRegion,
} from '../../services/fairs.services';
import { getMaxAmountOfFairs } from '../../services/regions.services';
import { usersService } from '../../services/users.service';
import { useAuth } from '../../contexts/AuthContext';
import {
  MoreVertical,
  Trash2,
  Pencil,
  PlayCircle,
  PauseCircle,
  FileText,
  Mail,
} from 'lucide-react';
import { Timestamp } from 'firebase/firestore';
import { FairStatus } from '../../types/fair.types';
import FairReportsDialog from './FairReportsDialog';
import { EmailPopup } from './EmailPopup';

// Fair Interface
interface Fair {
  id: string;
  name: string;
  addressLine: string;
  city: string;
  state: string;
  zipcode: string;
  registrationStartDate: string | Timestamp;
  registrationEndDate: string | Timestamp;
  fairStartDateTime: string | Timestamp;
  fairStatus: FairStatus;
}

const FairManagement: React.FC = () => {
  //Fair Details
  const [fairName, setFairName] = useState('');
  const [addressLine, setAddressLine] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [zipcode, setZipcode] = useState('');
  const [registrationStartDate, setRegistrationStartDate] = useState('');
  const [registrationEndDate, setRegistrationEndDate] = useState('');
  const [fairStartDateTime, setFairStartDateTime] = useState('');
  //Region Details for Fairs
  const [numberOfFairs, setNumberOfFairs] = useState(0);
  const [maxAmountOfFairs, setMaxAmountOfFairs] = useState(0);
  //Dialogs and hook variables
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [startStopDialog, setStartStopDialog] = useState(false);
  const [startStopMessage, setStartStopMessage] = useState('');
  const [startStopDecision, setStartStopDecision] = useState('');
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [currentFair, setCurrentFair] = useState<Fair | null>(null);
  const [fairs, setFairs] = useState<Fair[]>([]);
  const [fairToDelete, setFairToDelete] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [error, setError] = useState('');
  // Action Menu
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedFair, setSelectedFair] = useState<Fair | null>(null);
  // Reports Dialog
  const [reportsDialogOpen, setReportsDialogOpen] = useState(false);
  //Authentication
  const { authStatus } = useAuth();

  //Open table menu with different actions (edit, delete, start/stop)
  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, fair: Fair) => {
    setAnchorEl(event.currentTarget);
    setSelectedFair(fair);
  };

  //Close table menu
  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedFair(null);
  };

  //Open reports dialog
  const handleOpenReportsDialog = () => {
    setReportsDialogOpen(true);
    handleMenuClose();
  };

  //Close reports dialog
  const handleCloseReportsDialog = () => {
    setReportsDialogOpen(false);
  };

  //Get the status of the fair and set the color of the chip accordingly
  const getStatusChipColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'suspended':
        return 'error';
      case 'inProgress':
        return 'warning';
      case 'completed':
        return 'info';
      default:
        return 'default';
    }
  };

  //Fetch the user region from the database
  const fetchUserRegion = useCallback(async () => {
    try {
      const region = await usersService.getUserRegion(
        authStatus.user?.uid ?? ''
      );
      return region;
    } catch (err) {
      console.error('Error fetching user region:', err);
      return null;
    }
  }, [authStatus.user?.uid]);

  //Fetch the number of fairs currently in the region and the maximum allowed fairs
  const fetchFairsCount = useCallback(async () => {
    try {
      //Count the number of fairs in the user's region
      const fairCount = await countFairsInRegion(await fetchUserRegion());
      setNumberOfFairs(fairCount);
    } catch (err) {
      console.error('Error fetching fairs count:', err);
    }
    try {
      //Get the maximum number of fairs allowed in the user's region
      const maxFairs = await getMaxAmountOfFairs(await fetchUserRegion());
      setMaxAmountOfFairs(maxFairs);
    } catch (err) {
      console.error('Error fetching user region:', err);
    }
  }, [fetchUserRegion]);

  //Fetch all fairs in the user's region
  const fetchFairs = useCallback(async () => {
    try {
      //Get the fairs in the user's region
      const rawData = await getFairs(await fetchUserRegion());

      //Map the raw data to the Fair interface
      const data: Fair[] = rawData.map((fair: any) => ({
        id: fair.id,
        name: fair.name,
        addressLine: fair.addressLine,
        city: fair.addressCity,
        state: fair.addressState,
        zipcode: fair.addressZip,
        registrationStartDate: fair.registrationStart,
        registrationEndDate: fair.registrationEnd,
        fairStartDateTime: fair.startTime,
        fairStatus: fair.status,
      }));

      //Set the fairs in the state
      setFairs(data);
    } catch (err) {
      console.error('Error fetching fairs:', err);
    }
  }, [fetchUserRegion]);

  //Fetch fairs and count on component mount
  useEffect(() => {
    fetchFairs();
    fetchFairsCount();
  }, [fetchFairs, fetchFairsCount]);

  //Open the create fair dialog and check if the user has reached the maximum number of fairs allowed in their region
  const handleCreateFairDialog = () => {
    //If the user has reached the maximum number of fairs allowed in their region, show an error message
    if (numberOfFairs >= maxAmountOfFairs) {
      setError(
        'You have reached the maximum number of fairs allowed in your region.'
      );
      //Close the dialog since the user cannot create a new fair
      setCreateDialogOpen(false);
    } else {
      //Open the create fair dialog and reset the form fields
      resetFairForm();
      setCreateDialogOpen(true);
      setError('');
    }
  };

  //Validate date sequences
  const validateDates = () => {
    //Check if dates are valid
    if (!registrationStartDate || !registrationEndDate || !fairStartDateTime) {
      setError('All date fields are required.');
      return false;
    }

    //Convert string dates to Date objects
    const startDate = new Date(registrationStartDate);
    const endDate = new Date(registrationEndDate);
    const fairDate = new Date(fairStartDateTime);

    //Esnure registration end date is after start date
    if (endDate <= startDate) {
      setError('Registration end date must be after registration start date.');
      return false;
    }

    //Ensure fair start date is after registration end date
    if (fairDate <= endDate) {
      setError('Fair start date must be after registration end date.');
      return false;
    }

    //If all checks pass, return true and allow the fair to be created
    return true;
  };

  //Handle the creation of a fair
  const handleCreateFair = async () => {
    if (
      !fairName ||
      !addressLine ||
      !city ||
      !state ||
      !zipcode ||
      !registrationStartDate ||
      !registrationEndDate ||
      !fairStartDateTime
    ) {
      setError('All fields are required.');
      return;
    }

    //Validate the dates before creating the fair (Ensure dates are in the correct order)
    if (!validateDates()) {
      return;
    }

    try {
      //Fetch the user's region and create the fair with the provided details
      const region = await fetchUserRegion();
      //Create the fair in the database
      await createFair(
        fairName,
        addressLine,
        city,
        state,
        zipcode,
        new Date(registrationStartDate),
        new Date(registrationEndDate),
        Timestamp.fromDate(new Date(fairStartDateTime)),
        region
      );
      //Refresh the fairs list and count
      fetchFairs();
      fetchFairsCount();
      //Close the create fair dialog
      setCreateDialogOpen(false);
      //Reset the form fields
      resetFairForm();
    } catch (err) {
      console.error('Error creating fair:', err);
      setError('Failed to create fair.');
    }
  };

  //Reset the form fields after creating or editing a fair
  const resetFairForm = () => {
    setFairName('');
    setAddressLine('');
    setCity('');
    setState('');
    setZipcode('');
    setRegistrationStartDate('');
    setRegistrationEndDate('');
    setFairStartDateTime('');
  };

  //Open the delete fair dialog and set the fair to be deleted
  const handleOpenDeleteDialog = (fairId: string) => {
    setFairToDelete(fairId);
    setDeleteDialogOpen(true);
    handleMenuClose();
  };

  //Handles the deletion of a fair
  const handleDeleteFair = async () => {
    //Check if the fair to be deleted is set
    if (!fairToDelete) return;
    try {
      //Delete the fair from the database
      await deleteFair(fairToDelete);
      //Refresh the fairs list and count
      fetchFairs();
      fetchFairsCount();
      //Close the delete fair dialog
      setDeleteDialogOpen(false);
      //Reset the fair to be deleted
      setFairToDelete(null);
    } catch (err) {
      console.error('Error deleting fair:', err);
    }
  };

  //Convert Firestore timestamp to YYYY-MM-DD format for date inputs
  const formatDateForInput = (dateValue: any) => {
    try {
      let date;

      //Handle Firestore Timestamp
      if (dateValue && typeof dateValue === 'object' && 'toDate' in dateValue) {
        date = dateValue.toDate();
      } else if (dateValue) {
        date = new Date(dateValue);
      } else {
        return '';
      }

      if (isNaN(date.getTime())) return '';

      return date.toISOString().split('T')[0];
    } catch (error) {
      console.error('Error formatting date for input:', error);
      return '';
    }
  };

  //Convert Firestore timestamp to YYYY-MM-DDThh:mm format for datetime-local inputs
  const formatDateTimeForInput = (dateTimeValue: any): string => {
    try {
      let dateTime: Date;

      //Handle Firestore Timestamp
      if (
        dateTimeValue &&
        typeof dateTimeValue === 'object' &&
        'toDate' in dateTimeValue
      ) {
        dateTime = dateTimeValue.toDate();
      } else if (dateTimeValue) {
        dateTime = new Date(dateTimeValue);
      } else {
        return '';
      }

      if (isNaN(dateTime.getTime())) return '';

      return dateTime.toISOString().slice(0, 16);
    } catch (error) {
      console.error('Error formatting datetime for input:', error);
      return '';
    }
  };

  //Format date for display
  const formatDate = (dateValue: any) => {
    try {
      // Handle Firestore Timestamp
      if (dateValue && typeof dateValue === 'object' && 'toDate' in dateValue) {
        return dateValue.toDate().toLocaleDateString();
      }

      // Handle string dates in format "April 22, 2025 at 7:00:00 PM UTC-5"
      if (typeof dateValue === 'string' && dateValue.includes('at')) {
        // Parse the formatted date string
        return new Date(dateValue).toLocaleDateString();
      }

      // Handle Date object or other string formats
      if (dateValue) {
        const date = new Date(dateValue);
        if (!isNaN(date.getTime())) {
          return date.toLocaleDateString();
        }
      }

      return 'N/A';
    } catch (error) {
      console.error('Error formatting date:', error, 'Value was:', dateValue);
      return 'Invalid Date';
    }
  };

  // Format datetime for display
  const formatDateTime = (dateTimeValue: any) => {
    try {
      // Handle Firestore Timestamp
      if (
        dateTimeValue &&
        typeof dateTimeValue === 'object' &&
        'toDate' in dateTimeValue
      ) {
        return dateTimeValue.toDate().toLocaleString();
      }

      // Handle Date object or string
      if (dateTimeValue) {
        const dateTime = new Date(dateTimeValue);
        if (!isNaN(dateTime.getTime())) {
          return dateTime.toLocaleString();
        }
      }

      return 'N/A';
    } catch (error) {
      console.error('Error formatting datetime:', error);
      return 'Invalid Date';
    }
  };
  //Open edit dialog and populate form with fair data
  const handleOpenEditDialog = (fair: Fair) => {
    //Set the current fair to be edited and populate the form fields with its data
    setCurrentFair(fair);
    setFairName(fair.name);
    setAddressLine(fair.addressLine);
    setCity(fair.city);
    setState(fair.state);
    setZipcode(fair.zipcode);
    setRegistrationStartDate(formatDateForInput(fair.registrationStartDate));
    setRegistrationEndDate(formatDateForInput(fair.registrationEndDate));
    setFairStartDateTime(formatDateTimeForInput(fair.fairStartDateTime));
    //Open the edit dialog, reset any error messages, and close the menu
    setEditDialogOpen(true);
    setError('');
    handleMenuClose();
  };

  //Handle updating a fair
  const handleUpdateFair = async () => {
    //Check if the current fair is set
    if (!currentFair) return;

    //Check if all fields are filled out
    if (
      !fairName ||
      !addressLine ||
      !city ||
      !state ||
      !zipcode ||
      !registrationStartDate ||
      !registrationEndDate ||
      !fairStartDateTime
    ) {
      setError('All fields are required.');
      return;
    }

    //Validate the dates before updating the fair (Ensure dates are in the correct order)
    if (!validateDates()) {
      return;
    }

    //Update the fair in the database with the provided details
    try {
      await updateFair(
        currentFair.id,
        fairName,
        addressLine,
        city,
        state,
        zipcode,
        new Date(registrationStartDate),
        new Date(registrationEndDate),
        Timestamp.fromDate(new Date(fairStartDateTime))
      );
      //Refresh the fairs list and count
      fetchFairs();
      setEditDialogOpen(false);
      //Close the edit dialog and reset the form fields
      resetFairForm();
      setCurrentFair(null);
    } catch (err) {
      console.error('Error updating fair:', err);
      setError('Failed to update fair.');
    }
  };

  //Open start-stop dialog and populate form with fair name
  const handleOpenStartStopDialog = (fair: Fair) => {
    //Check if the fair is active or in progress before allowing start/stop actions
    if (fair.fairStatus === 'active' || fair.fairStatus === 'inProgress') {
      //Set the current fair to be started/stopped and populate the form with its name
      setCurrentFair(fair);
      setFairName(fair.name);
      //Open the start/stop dialog, reset any error messages, and close the menu
      setStartStopDialog(true);
      setError('');
      handleMenuClose();

      //Set the start/stop decision and message based on the fair's status
      if (fair.fairStatus === 'active') {
        //If the status is 'active', set the decision to 'Start' and the message
        setStartStopDecision('Start');
        setStartStopMessage(
          'Are you sure you want to start the ' + fair.name + ' Fair?'
        );
      }
      //If the status is 'inProgress', set the decision to 'Stop' and the message
      if (fair.fairStatus === 'inProgress') {
        //If the status is 'inProgress', set the decision to 'Stop' and the message
        setStartStopDecision('Stop');
        setStartStopMessage(
          'Are you sure you want to stop the ' + fair.name + ' Fair?'
        );
      }
    } else {
      setError('This fair cannot be started or stopped with this status.');
      handleMenuClose();
      return;
    }
  };

  //Handle the start/stop action for a fair
  const handleStartStopFair = async () => {
    //Check if the current fair is set
    if (!currentFair) return;
    try {
      //Start or stop the fair based on the decision made in the dialog
      startStopFair(currentFair.id, startStopDecision);
      //Refresh the fairs list
      fetchFairs();
      //Close the start/stop dialog and reset the form fields
      setStartStopDialog(false);
      setCurrentFair(null);
    } catch (err) {
      console.error('Error starting/stopping fair:', err);
      setError('Failed to start/stop fair.');
    }
  };

  //Handle date changes with validation
  const handleRegistrationEndDateChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newEndDate = e.target.value;
    setRegistrationEndDate(newEndDate);

    //Clear error if dates are now valid
    if (registrationStartDate && newEndDate && fairStartDateTime) {
      const startDate = new Date(registrationStartDate);
      const endDate = new Date(newEndDate);
      const fairDate = new Date(fairStartDateTime);

      if (endDate <= startDate) {
        setError(
          'Registration end date must be after registration start date.'
        );
      } else if (fairDate <= endDate) {
        setError('Fair start date must be after registration end date.');
      } else {
        setError('');
      }
    }
  };

  //Handle fair start date change with validation
  const handleFairStartDateChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newFairDate = e.target.value;
    setFairStartDateTime(newFairDate);

    //Clear error if dates are now valid
    if (registrationStartDate && registrationEndDate && newFairDate) {
      const endDate = new Date(registrationEndDate);
      const fairDate = new Date(newFairDate);

      if (fairDate <= endDate) {
        setError('Fair start date must be after registration end date.');
      } else {
        setError('');
      }
    }
  };

  //Open email dialog
  const handleOpenEmailDialog = (fair: Fair) => {
    setCurrentFair(fair);
    setEmailDialogOpen(true);
    handleMenuClose();
  };

  //Handle registration start date change with validation
  const handleRegistrationStartDateChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newStartDate = e.target.value;
    setRegistrationStartDate(newStartDate);

    //Clear error if dates are now valid
    if (newStartDate && registrationEndDate) {
      const startDate = new Date(newStartDate);
      const endDate = new Date(registrationEndDate);

      if (endDate <= startDate) {
        setError(
          'Registration end date must be after registration start date.'
        );
      } else {
        setError('');
      }
    }
  };

  //Clear error after 5 seconds
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        setError('');
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Fair Management
      </Typography>

      {/* Error display at the top of the main page */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          marginRight: 4,
          alignItems: 'flex-end',
        }}
      >
        <Button variant="contained" onClick={handleCreateFairDialog}>
          Create New Fair
        </Button>

        <Typography variant="h6" gutterBottom>
          Maximum Fairs in Region: {numberOfFairs} / {maxAmountOfFairs}
        </Typography>
      </Box>

      {/* Fair Table */}
      <TableContainer component={Paper} sx={{ mt: 2 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Location</TableCell>
              <TableCell>Registration Period</TableCell>
              <TableCell>Fair Start Date & Time</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {fairs.map((fair) => (
              <TableRow key={fair.id}>
                <TableCell>{fair.name}</TableCell>
                <TableCell
                  sx={{
                    maxWidth: 300,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {fair.addressLine}, {fair.city}, {fair.state} {fair.zipcode}
                </TableCell>
                <TableCell>
                  {formatDate(fair.registrationStartDate)} -{' '}
                  {formatDate(fair.registrationEndDate)}
                </TableCell>
                <TableCell>{formatDateTime(fair.fairStartDateTime)}</TableCell>
                <TableCell>
                  <Chip
                    label={
                      fair.fairStatus === 'inProgress'
                        ? 'In Progress'
                        : fair.fairStatus
                    }
                    color={getStatusChipColor(fair.fairStatus)}
                    size="small"
                  />
                </TableCell>
                <TableCell align="center">
                  {/* Actions Menu Button */}
                  <IconButton
                    onClick={(event) => handleMenuOpen(event, fair)}
                    size="small"
                  >
                    <MoreVertical size={20} />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        keepMounted
      >
        <MenuItem
          onClick={() => selectedFair && handleOpenEmailDialog(selectedFair)}
          sx={{ gap: 1 }}
        >
          <Mail size={16} />
          Email
        </MenuItem>
        <MenuItem
          onClick={() => selectedFair && handleOpenEditDialog(selectedFair)}
          sx={{ gap: 1 }}
        >
          <Pencil size={16} />
          Edit
        </MenuItem>
        <MenuItem onClick={handleOpenReportsDialog} sx={{ gap: 1 }}>
          <FileText size={16} />
          Reports
        </MenuItem>
        {selectedFair &&
          (selectedFair.fairStatus === 'active' ||
            selectedFair.fairStatus === 'inProgress') && (
            <MenuItem
              onClick={() =>
                selectedFair && handleOpenStartStopDialog(selectedFair)
              }
              sx={{ gap: 1 }}
            >
              {selectedFair.fairStatus === 'active' ? (
                //Fair Start only appears with an active status
                <>
                  <PlayCircle size={16} />
                  Start Fair
                </>
              ) : (
                //Fair Stop only appears with an inProgress status
                <>
                  <PauseCircle size={16} />
                  Stop Fair
                </>
              )}
            </MenuItem>
          )}
        <MenuItem
          onClick={() =>
            selectedFair && handleOpenDeleteDialog(selectedFair.id)
          }
          sx={{ color: 'error.main', gap: 1 }}
        >
          <Trash2 size={16} />
          Delete
        </MenuItem>
      </Menu>

      {/* Create Fair Dialog */}
      <Dialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Add a New Fair</DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                label="Fair Name"
                fullWidth
                variant="outlined"
                margin="dense"
                value={fairName}
                onChange={(e) => setFairName(e.target.value)}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Address Line"
                fullWidth
                variant="outlined"
                margin="dense"
                value={addressLine}
                onChange={(e) => setAddressLine(e.target.value)}
              />
            </Grid>

            <Grid item xs={4}>
              <TextField
                label="City"
                fullWidth
                variant="outlined"
                margin="dense"
                value={city}
                onChange={(e) => setCity(e.target.value)}
              />
            </Grid>

            <Grid item xs={4}>
              <TextField
                label="State"
                fullWidth
                variant="outlined"
                margin="dense"
                value={state}
                onChange={(e) => setState(e.target.value)}
              />
            </Grid>

            <Grid item xs={4}>
              <TextField
                label="Zipcode"
                fullWidth
                variant="outlined"
                margin="dense"
                value={zipcode}
                onChange={(e) => setZipcode(e.target.value)}
              />
            </Grid>

            <Grid item xs={6}>
              <TextField
                label="Registration Start Date"
                type="date"
                fullWidth
                variant="outlined"
                margin="dense"
                InputLabelProps={{ shrink: true }}
                value={registrationStartDate}
                onChange={handleRegistrationStartDateChange}
              />
            </Grid>

            <Grid item xs={6}>
              <TextField
                label="Registration End Date"
                type="date"
                fullWidth
                variant="outlined"
                margin="dense"
                InputLabelProps={{ shrink: true }}
                value={registrationEndDate}
                onChange={handleRegistrationEndDateChange}
                helperText={
                  registrationStartDate &&
                  registrationEndDate &&
                  new Date(registrationEndDate) <=
                    new Date(registrationStartDate)
                    ? 'End date must be after start date'
                    : ''
                }
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Fair Start Date & Time"
                type="datetime-local"
                fullWidth
                variant="outlined"
                margin="dense"
                InputLabelProps={{ shrink: true }}
                value={fairStartDateTime}
                onChange={handleFairStartDateChange}
                helperText={
                  registrationEndDate &&
                  fairStartDateTime &&
                  new Date(fairStartDateTime) <= new Date(registrationEndDate)
                    ? 'Fair start must be after registration end date'
                    : ''
                }
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleCreateFair}
            variant="contained"
            color="primary"
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Fair Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Edit Fair</DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                label="Fair Name"
                fullWidth
                variant="outlined"
                margin="dense"
                value={fairName}
                onChange={(e) => setFairName(e.target.value)}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Address Line"
                fullWidth
                variant="outlined"
                margin="dense"
                value={addressLine}
                onChange={(e) => setAddressLine(e.target.value)}
              />
            </Grid>

            <Grid item xs={4}>
              <TextField
                label="City"
                fullWidth
                variant="outlined"
                margin="dense"
                value={city}
                onChange={(e) => setCity(e.target.value)}
              />
            </Grid>

            <Grid item xs={4}>
              <TextField
                label="State"
                fullWidth
                variant="outlined"
                margin="dense"
                value={state}
                onChange={(e) => setState(e.target.value)}
              />
            </Grid>

            <Grid item xs={4}>
              <TextField
                label="Zipcode"
                fullWidth
                variant="outlined"
                margin="dense"
                value={zipcode}
                onChange={(e) => setZipcode(e.target.value)}
              />
            </Grid>

            <Grid item xs={6}>
              <TextField
                label="Registration Start Date"
                type="date"
                fullWidth
                variant="outlined"
                margin="dense"
                InputLabelProps={{ shrink: true }}
                value={registrationStartDate}
                onChange={handleRegistrationStartDateChange}
              />
            </Grid>

            <Grid item xs={6}>
              <TextField
                label="Registration End Date"
                type="date"
                fullWidth
                variant="outlined"
                margin="dense"
                InputLabelProps={{ shrink: true }}
                value={registrationEndDate}
                onChange={handleRegistrationEndDateChange}
                helperText={
                  registrationStartDate &&
                  registrationEndDate &&
                  new Date(registrationEndDate) <=
                    new Date(registrationStartDate)
                    ? 'End date must be after start date'
                    : ''
                }
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Fair Start Date & Time"
                type="datetime-local"
                fullWidth
                variant="outlined"
                margin="dense"
                InputLabelProps={{ shrink: true }}
                value={fairStartDateTime}
                onChange={handleFairStartDateChange}
                helperText={
                  registrationEndDate &&
                  fairStartDateTime &&
                  new Date(fairStartDateTime) <= new Date(registrationEndDate)
                    ? 'Fair start must be after registration end date'
                    : ''
                }
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleUpdateFair}
            variant="contained"
            color="primary"
          >
            Update
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          Are you sure you want to delete this fair?
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteFair} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Start-Stop Confirmation Dialog */}
      <Dialog open={startStopDialog} onClose={() => setStartStopDialog(false)}>
        <DialogTitle>Confirm Fair {startStopDecision}</DialogTitle>
        <DialogContent>{startStopMessage}</DialogContent>
        <DialogContent>Please be sure, this can not be undone.</DialogContent>
        <DialogActions>
          <Button onClick={() => setStartStopDialog(false)}>Cancel</Button>
          <Button
            onClick={handleStartStopFair}
            color="primary"
            variant="contained"
          >
            {startStopDecision}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reports Dialog */}
      <FairReportsDialog
        open={reportsDialogOpen}
        onClose={handleCloseReportsDialog}
        fair={selectedFair}
      />

      {/* Email Popup */}
      <EmailPopup
        open={emailDialogOpen}
        onClose={() => setEmailDialogOpen(false)}
        fairName={currentFair?.name}
        fairId={currentFair?.id}
      />
    </Box>
  );
};

export default FairManagement;
